"use client";
import React, { useState } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
    // Phone,
    // Mail,
    // MessageCircle,
  HelpCircle,
  // Book,
  // Shield,
  // FileText,
  // Users,
} from "lucide-react";
// import { CONTACT_US_DATA } from "@/lib/ConstData";
// import { useAnalytics } from "@/hooks/useAnalytics";
// import { ContactAnalytics } from "@/lib/analyticsName";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  tags: string[];
}

const FAQClient: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // const { createClickHandler } = useAnalytics();

  const faqData: FAQItem[] = [
    {
      id: 1,
      question: "How do I book a bike rental?",
      answer:
        "Booking a bike is simple! Visit our website or mobile app, select your city, choose your preferred bike, pick your rental dates, and complete the payment. You'll receive a confirmation with delivery details.",
      category: "Booking",
      tags: ["booking", "rental", "process"],
    },
    {
      id: 2,
      question: "What documents do I need to rent a bike?",
      answer:
        "You'll need a valid driving license, government-issued ID proof (Aadhar card, PAN card, or passport), and a credit/debit card for payment. For international travelers, a valid international driving permit is required.",
      category: "Documents",
      tags: ["documents", "license", "ID proof"],
    },
    {
      id: 3,
      question: "What is the minimum rental period?",
      answer:
        "Our minimum rental period is 24 hours (1 day). We also offer hourly rentals for short trips, weekly and monthly packages for longer stays, and corporate rentals for business needs.",
      category: "Rental Period",
      tags: ["duration", "minimum", "packages"],
    },
    {
      id: 4,
      question: "Do you provide helmets and safety gear?",
      answer:
        "Yes! We provide high-quality helmets, reflective vests, and basic safety gear with every rental at no additional cost. Safety is our top priority, and we ensure all equipment meets safety standards.",
      category: "Safety",
      tags: ["helmets", "safety gear", "equipment"],
    },
    {
      id: 5,
      question: "What happens if the bike breaks down?",
      answer:
        "We provide 24/7 roadside assistance. If your bike breaks down, call our emergency number immediately. We'll try to provide assistance and make necessary arrangements.",
      category: "Support",
      tags: ["breakdown", "assistance", "emergency"],
    },
    {
      id: 6,
      question: "Is insurance included in the rental?",
      answer:
        "Yes, comprehensive insurance is included in all our rentals. This covers third-party liability, theft, and damage to the vehicle. We also offer additional personal accident coverage for riders.",
      category: "Insurance",
      tags: ["insurance", "coverage", "protection"],
    },
    {
      id: 7,
      question: "Can I extend my rental period?",
      answer:
        "Absolutely! You can extend your rental by calling our customer service or through the mobile app. Extensions are subject to bike availability and must be requested at least 4 hours before the current rental ends.",
      category: "Extensions",
      tags: ["extend", "duration", "flexibility"],
    },
    {
      id: 8,
      question: "What are your delivery and pickup options?",
      answer:
        "We offer doorstep delivery and pickup at your preferred location within city limits. Airport pickup/drop is also available. Delivery charges vary by location and are clearly displayed during booking.",
      category: "Delivery",
      tags: ["delivery", "pickup", "airport"],
    },
    {
      id: 9,
      question: "What is your cancellation policy?",
      answer:
        "1. No show means 100% deduction. 2. 0 - 6 hrs prior to pick up time, 75% rental charges will be withheld. 3. 6 - 24 hrs prior to pick up time, 50% rental charges will be withheld. 4. 24 - 48 hrs prior to pick up time, 25% rental charges will be withheld. 5. More than 48 hrs prior to pick up time, 10% rental charge will be withheld. 6. In the case, where cancellation is done by Ridengo due to unavailability of inventory the entire amount of booking shall be refunded to the user account. 7. All refunds shall be made through the original payment mode within 7-10 working days.",
      category: "Cancellation",
      tags: ["cancellation", "refund", "policy"],
    },
    {
      id: 10,
      question: "Do you offer corporate rentals?",
      answer:
        "Yes! We provide special corporate packages for businesses, including bulk discounts, dedicated account managers, and flexible payment terms. Perfect for employee transportation, client visits, and business events.",
      category: "Corporate",
      tags: ["corporate", "business", "bulk"],
    },
    {
      id: 11,
      question: "What cities do you operate in?",
      answer:
        "We currently operate in 40+ major cities across India including Delhi, Mumbai, Bangalore, Chennai, Hyderabad, Pune, Kolkata, and many more. Check our website for the complete list of cities.",
      category: "Locations",
      tags: ["cities", "coverage", "locations"],
    },
    {
      id: 12,
      question: "How do I report damage or issues?",
      answer:
        "Report any damage or issues immediately by calling our customer service or through the mobile app. Take photos if possible. We'll assess the situation and handle it according to our damage policy.",
      category: "Issues",
      tags: ["damage", "reporting", "problems"],
    },
    {
      id: 13,
      question: "What is your refund policy?",
      answer:
        "The refunded amount will reflect in the customer's bank account within 7-10 business days.",
      category: "Refund",
      tags: [],
    },
    {
      id: 14,
      question: "What is your payment policy?",
      answer:
        "We accept cash, credit/debit cards, and online payment methods. Payment is required at the time of booking.",
      category: "Payment",
      tags: ["payment", "methods", "policy"],
    },
  ];

  const categories = [
    { name: "All", count: faqData.length, color: "bg-gray-500" },
    {
      name: "Booking",
      count: faqData.filter((item) => item.category === "Booking").length,
      color: "bg-blue-500",
    },
    {
      name: "Documents",
      count: faqData.filter((item) => item.category === "Documents").length,
      color: "bg-green-500",
    },
    {
      name: "Safety",
      count: faqData.filter((item) => item.category === "Safety").length,
      color: "bg-orange-500",
    },
    {
      name: "Support",
      count: faqData.filter((item) => item.category === "Support").length,
      color: "bg-purple-500",
    },
    {
      name: "Insurance",
      count: faqData.filter((item) => item.category === "Insurance").length,
      color: "bg-red-500",
    },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setTimeout(() => setIsSearching(false), 1000);
  };

  const toggleItem = (id: number) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredFAQs = faqData.filter((item) => {
    const matchesSearch =
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      selectedCategory === "All" || item.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // const handleContactClick = (method: string) => {
  //   const clickHandler = createClickHandler(
  //     () => {},
  //     ContactAnalytics.Submit,
  //     ContactAnalytics.Category,
  //     { contact_method: method }
  //   );
  //   clickHandler();
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-4 h-4 bg-blue-400 rounded-full animate-bounce"></div>
          <div className="absolute top-40 right-20 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-purple-400 rounded-full animate-ping"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl md:text-6xl font-bold mb-8">
              Frequently Asked <span className="text-blue-200">Questions</span>
            </h1>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed">
              Find quick answers to common questions about bike rentals, booking
              process, safety, and our services.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search for questions, topics, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {isSearching ? "Searching..." : "Search"}
                </button>
              </div>
            </form>
          </div>
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-200 ${
                  selectedCategory === category.name
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                <span className="font-medium">{category.name}</span>
                <span className="text-sm opacity-75">({category.count})</span>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                No questions found
              </h2>
              <p className="text-gray-600">
                Try adjusting your search terms or category filter
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFAQs.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full px-6 py-6 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <HelpCircle className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          {item.question}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      {expandedItems.includes(item.id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {expandedItems.includes(item.id) && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="pt-4">
                        <p className="text-gray-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      {/* <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <HelpCircle className="w-16 h-16 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Can&apos;t find what you&apos;re looking for? Our support team is
            here to help you 24/7.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Call Us
              </h3>
              <p className="text-gray-600 mb-4">
                Available 24/7 for immediate assistance
              </p>
              <a
                href={`tel:${CONTACT_US_DATA.phone}`}
                onClick={() => handleContactClick("phone")}
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Call Now
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Email Support
              </h3>
              <p className="text-gray-600 mb-4">
                Get detailed responses within 2 hours
              </p>
              <a
                href={`mailto:${CONTACT_US_DATA.supportEmail}`}
                onClick={() => handleContactClick("email")}
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                Send Email
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Live Chat
              </h3>
              <p className="text-gray-600 mb-4">
                Chat with our experts in real-time
              </p>
              <button
                onClick={() => handleContactClick("chat")}
                className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors duration-200"
              >
                Start Chat
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* Quick Links Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Quick Links & Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find additional information and resources to help you make the
              most of your bike rental experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Book className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Rental Guide
              </h3>
              <p className="text-gray-600">Complete guide to bike rentals</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Safety Tips
              </h3>
              <p className="text-gray-600">Essential safety guidelines</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Terms & Conditions
              </h3>
              <p className="text-gray-600">Rental terms and policies</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Support Center
              </h3>
              <p className="text-gray-600">Help and troubleshooting</p>
            </div>
          </div>
        </div>
      </section> */}

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default FAQClient;
